import React, { FC, useContext, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import Button from "@natera/material/lib/button";
import Svg from "@natera/material/lib/svg";
import { Dialog } from "@natera/material/lib/dialog";
import { defineMessages, useIntl } from "react-intl";
import { APIContext, DialogsContext } from "@app/pippa/provider";
import {
  MAX_MESSAGE_FEEDBACK_LENGTH,
  MessageReaction,
} from "@app/pippa/models";
import TickAnimation from "@app/pippa/assets/svg/tick-animation.svg";
import "./messageFeedbackDialog.scss";

const SHOW_ANIMATION_DELAY_TIMEOUT = 120;
const FINISH_ANIMATION_DELAY_TIMEOUT = 2200;

const CLOSE_ANIMATION_TIMEOUT = 180;

const TEXTAREA_ID = "message-feedback-text";

const FOCUS_TRAP_OPTIONS = { skipInitialFocus: true };

const messages = defineMessages({
  messageFeedbackDialogTitle: {
    id: "pippa.messageFeedback.dialogTitle",
    defaultMessage: "Help us improve this response",
  },
  messageFeedbackDialogDescription: {
    id: "pippa.messageFeedback.dialogDescription",
    defaultMessage:
      "Your feedback helps us improve responses for everyone. Please don't include personal health info or identifiers.",
  },
  messageFeedbackDialogPlaceholder: {
    id: "pippa.messageFeedback.dialogPlaceholder",
    defaultMessage: "Tell us what went wrong with this response",
  },
  messageFeedbackDialogSubmit: {
    id: "pippa.messageFeedback.dialogSubmit",
    defaultMessage: "Send feedback",
  },
  messageFeedbackDialogSuccess: {
    id: "pippa.messageFeedback.dialogSuccess",
    defaultMessage: "Thank you for your feedback",
  },
});

interface Props {
  messageId: string;
}

const SuccessScreen: FC<{ onFinish: () => void }> = ({ onFinish }) => {
  const intl = useIntl();
  const [showAnimation, setShowAnimation] = useState(false);

  useEffect(() => {
    const animationDelay = setTimeout(
      () => setShowAnimation(true),
      SHOW_ANIMATION_DELAY_TIMEOUT
    );
    const closeDelay = setTimeout(onFinish, FINISH_ANIMATION_DELAY_TIMEOUT);
    return () => {
      clearTimeout(animationDelay);
      clearTimeout(closeDelay);
    };
  }, []);

  return (
    <div
      className={classNames("message-feedback-dialog__success", {
        "message-feedback-dialog__success--drawn": showAnimation,
      })}
      data-testid="message-feedback-success"
    >
      <Svg
        className="message-feedback-dialog__success-icon"
        content={TickAnimation}
        aria-hidden={true}
      />
      <p className="visually-hidden" aria-live="polite">
        {intl.formatMessage(messages.messageFeedbackDialogSuccess)}
      </p>
    </div>
  );
};

const MessageFeedbackDialog: FC<Props> = ({ messageId }) => {
  const intl = useIntl();
  const [text, setText] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const {
    apiProviderState: { chatId },
    submitMessageFeedback,
  } = useContext(APIContext);
  const { messageFeedbackDialog } = useContext(DialogsContext);
  const [isClosing, setIsClosing] = useState(false);
  const closeTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  useEffect(() => () => clearTimeout(closeTimeout.current), []);

  const close = () => {
    if (closeTimeout.current) return;
    setIsClosing(true);
    closeTimeout.current = setTimeout(
      () => messageFeedbackDialog.close(),
      CLOSE_ANIMATION_TIMEOUT
    );
  };

  const handleSubmit = async () => {
    if (!text.trim() || isSending) return;
    setIsSending(true);

    try {
      // A second THUMBS_DOWN event, this one carrying the text — the reaction row
      // recorded when the thumb was clicked is left untouched.
      await submitMessageFeedback({
        chatId,
        messageId,
        reaction: MessageReaction.THUMBS_DOWN,
        text,
      });
      setIsSent(true);
    } catch (error) {
      console.error("Failed to submit message feedback:", error);
    } finally {
      setIsSending(false);
    }
  };

  const isAtLimit = text.length === MAX_MESSAGE_FEEDBACK_LENGTH;

  const actions = isSent ? undefined : (
    <Button
      className="message-feedback-dialog__submit"
      raised
      loading={isSending}
      disabled={!text.trim()}
      onClick={handleSubmit}
    >
      {intl.formatMessage(messages.messageFeedbackDialogSubmit)}
    </Button>
  );

  return (
    <Dialog
      className={classNames(
        "message-feedback-dialog pippa-message-feedback-dialog",
        {
          "message-feedback-dialog--closing": isClosing,
          "message-feedback-dialog--sent": isSent,
        }
      )}
      onClose={close}
      actions={actions}
      title={
        isSent
          ? undefined
          : intl.formatMessage(messages.messageFeedbackDialogTitle)
      }
      focusTrapOptions={FOCUS_TRAP_OPTIONS}
      showCrossButton={!isSent}
    >
      {isSent ? (
        <SuccessScreen onFinish={close} />
      ) : (
        <div className="message-feedback-dialog__body">
          <p className="message-feedback-dialog__description">
            {intl.formatMessage(messages.messageFeedbackDialogDescription)}
          </p>
          <div className="message-feedback-dialog__field">
            <textarea
              id={TEXTAREA_ID}
              className="message-feedback-dialog__textarea"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={intl.formatMessage(
                messages.messageFeedbackDialogPlaceholder
              )}
              maxLength={MAX_MESSAGE_FEEDBACK_LENGTH}
              rows={4}
              disabled={isSending}
              aria-label={intl.formatMessage(
                messages.messageFeedbackDialogTitle
              )}
              aria-describedby={`${TEXTAREA_ID}-char-count`}
            />
            <div
              id={`${TEXTAREA_ID}-char-count`}
              className={classNames("message-feedback-dialog__char-count", {
                "message-feedback-dialog__char-count--at-limit": isAtLimit,
              })}
              aria-live="polite"
            >
              {text.length}/{MAX_MESSAGE_FEEDBACK_LENGTH}
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
};

export default MessageFeedbackDialog;
